/**
 * Lever detail drawer — the full table behind a lever card, opened over the centre (never
 * inside the rail). Reads the same `ScenarioContext`/`ScenarioResult` the card summarises.
 */

import {
  deferFigures,
  familiesKeptForCount,
  type Scenario5LeverId,
  type ScenarioContext,
  type ScenarioParams,
  type ScenarioResult,
} from "@/lib/data";

import {
  CONV_CERTIFIED_MAX,
  familyMdrStatus,
  familyWaiverAccounts,
  FLOOR_ENVELOPE_M2,
  PACK_WAIVER_THRESHOLD,
  SYSTEMS,
} from "./mock";

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

export function LeverDrawer({
  lever,
  draft,
  result,
  ctx,
  onClose,
}: {
  lever: Scenario5LeverId | null;
  draft: ScenarioParams;
  result: ScenarioResult;
  ctx: ScenarioContext;
  onClose: () => void;
}) {
  if (!lever) {
    return null;
  }
  return (
    <div className="drawer on">
      <div className="drawer-head">
        <div className="t">{titleFor(lever)}</div>
        <div className="s">{subFor(lever, draft, result, ctx)}</div>
        <button className="drawer-close" onClick={onClose}>
          ✕
        </button>
      </div>
      <div className="drawer-body">
        {lever === "relo" ? <ReloTable draft={draft} result={result} ctx={ctx} /> : null}
        {lever === "pack" ? <PackTable draft={draft} ctx={ctx} /> : null}
        {lever === "conv" ? <ConvTable draft={draft} ctx={ctx} /> : null}
        {lever === "cap" ? <CapTable result={result} /> : null}
        {lever === "pace" ? <PaceTable draft={draft} ctx={ctx} /> : null}
      </div>
    </div>
  );
}

function titleFor(l: Scenario5LeverId) {
  return {
    relo: "Relocate non-movers",
    pack: "Pack rationalize",
    conv: "Rx to Dx convert",
    cap: "Add pick-face capacity",
    pace: "Pace the NPI intake",
  }[l];
}
function subFor(
  l: Scenario5LeverId,
  draft: ScenarioParams,
  result: ScenarioResult,
  ctx: ScenarioContext,
) {
  if (l === "relo") {
    return draft.reloThreshold < 0
      ? "off"
      : `≤ ${draft.reloThreshold} picks/yr · ${fmt(result.relocate)} SKUs qualify`;
  }
  if (l === "pack") {
    return `${draft.packCount} of ${ctx.families.pack.length} families selected · ${fmt(result.pack)} faces released`;
  }
  if (l === "conv") {
    return `${draft.convCount} of ${ctx.families.conv.length} sets selected · ${fmt(result.conv)} faces released`;
  }
  if (l === "cap") {
    return result.addFaces > 0 ? `ceiling ${fmt(result.ceiling)}` : "nothing added";
  }
  const defer = deferFigures(draft.pacePct, ctx.model);
  return `${draft.pacePct}% of the ${defer.pinch} intake deferred`;
}

function ReloTable({
  draft,
  result,
  ctx,
}: {
  draft: ScenarioParams;
  result: ScenarioResult;
  ctx: ScenarioContext;
}) {
  if (draft.reloThreshold < 0) {
    return (
      <div className="drawer-note">
        The lever is off. Set a velocity threshold and this table fills with the SKUs it selects.
      </div>
    );
  }
  const claimed = ctx.skus
    .filter((s) => result.claim.get(s.id) === "relo")
    .slice()
    .sort((a, b) => b.inv - a.inv)
    .slice(0, 150);
  return (
    <>
      <table className="ptable">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Description</th>
            <th>Band</th>
            <th className="num">Boxes/mo</th>
            <th className="num">Stock</th>
            <th>Destination</th>
          </tr>
        </thead>
        <tbody>
          {claimed.map((s) => (
            <tr key={s.id}>
              <td>{s.id}</td>
              <td>{s.name}</td>
              <td>{s.band}</td>
              <td className="num">{s.vel}</td>
              <td className="num">{fmt(s.inv)}</td>
              <td>PLDC</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="drawer-note">
        Real SKUs the relocate lever claims, largest stock first (sample of {claimed.length} of{" "}
        {fmt(result.relocate)}). Relocation is repositioning, not disposal.
      </div>
    </>
  );
}

function PackTable({ draft, ctx }: { draft: ScenarioParams; ctx: ScenarioContext }) {
  const families = ctx.families.pack.slice().sort((a, b) => b.base - a.base);
  const kept = new Set(
    familiesKeptForCount(families, draft.packCount, draft.packExcluded).map((f) => f.id),
  );
  const waivers = familyWaiverAccounts(families);
  return (
    <>
      <table className="ptable">
        <thead>
          <tr>
            <th>Family</th>
            <th className="num">Faces released</th>
            <th>Contracted range</th>
          </tr>
        </thead>
        <tbody>
          {families.map((f) => {
            const on = kept.has(f.id);
            const waiverAccount = waivers[f.id];
            return (
              <tr key={f.id} className={on ? undefined : "dim"}>
                <td>{f.label}</td>
                <td className="num">{on ? fmt(f.base) : "—"}</td>
                <td>
                  {waiverAccount ? <span className="flag">waiver · {waiverAccount}</span> : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="drawer-note">
        Three 30-packs collapse into one 90-pack, so the lever touches more SKUs than it releases
        faces. Families are ordered by faces released; selecting {PACK_WAIVER_THRESHOLD}+ triggers
        the two named waivers.
      </div>
    </>
  );
}

function ConvTable({ draft, ctx }: { draft: ScenarioParams; ctx: ScenarioContext }) {
  const sets = ctx.families.conv.slice().sort((a, b) => b.base - a.base);
  const kept = new Set(
    familiesKeptForCount(sets, draft.convCount, draft.convExcluded).map((f) => f.id),
  );
  const mdr = familyMdrStatus(sets);
  return (
    <>
      <table className="ptable">
        <thead>
          <tr>
            <th>Fitting set</th>
            <th className="num">Faces released</th>
            <th>EU MDR status</th>
          </tr>
        </thead>
        <tbody>
          {sets.map((f) => {
            const on = kept.has(f.id);
            const status = mdr[f.id] ?? "certified";
            return (
              <tr key={f.id} className={on ? undefined : "dim"}>
                <td>{f.label}</td>
                <td className="num">{on ? fmt(f.base) : "—"}</td>
                <td>
                  {status === "certified" ? (
                    "Certified"
                  ) : (
                    <span className={on ? "flag" : ""}>{status}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="drawer-note">
        The first {CONV_CERTIFIED_MAX} sets by real release value are certified. Sets beyond that
        are pending or legacy under EU MDR and need a regulatory check before they can convert.
      </div>
    </>
  );
}

function CapTable({ result }: { result: ScenarioResult }) {
  return (
    <>
      <table className="ptable">
        <thead>
          <tr>
            <th>System</th>
            <th className="num">Faces per unit</th>
            <th className="num">Cost</th>
            <th className="num">Lead time</th>
            <th className="num">m²</th>
            <th>Suits</th>
          </tr>
        </thead>
        <tbody>
          {SYSTEMS.map((s) => (
            <tr key={s.key}>
              <td>{s.name}</td>
              <td className="num">+{fmt(s.addFacesFraction * result.ceiling)}</td>
              <td className="num">£{s.addCostM}m</td>
              <td className="num">{s.addLeadMonths} mo</td>
              <td className="num">{s.addM2}</td>
              <td>{s.affinity}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="drawer-note">
        This lever raises the ceiling. The other four reduce what is held — they are not the same
        thing and the screen never adds them together. Free envelope:{" "}
        <b>{fmt(FLOOR_ENVELOPE_M2)}</b> m² total.
      </div>
    </>
  );
}

function PaceTable({ draft, ctx }: { draft: ScenarioParams; ctx: ScenarioContext }) {
  const defer = deferFigures(draft.pacePct, ctx.model);
  const total = ctx.model.npiFamilies
    .filter((f) => f.rel === defer.pinch)
    .reduce((a, f) => a + f.total, 0);
  return (
    <>
      <table className="ptable">
        <thead>
          <tr>
            <th>Launch</th>
            <th className="num">{defer.pinch} slots</th>
            <th className="num">Stays in {defer.pinch}</th>
            <th className="num">To {defer.next?.year ?? "—"}</th>
            <th className="num">To {defer.after?.year ?? "—"}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>NPI intake</td>
            <td className="num">{fmt(total)}</td>
            <td className="num">{fmt(total - defer.defer)}</td>
            <td className="num">{defer.next ? `+${fmt(defer.next.amount)}` : "—"}</td>
            <td className="num">{defer.after ? `+${fmt(defer.after.amount)}` : "—"}</td>
          </tr>
        </tbody>
      </table>
      <div className="drawer-note">
        Pacing the intake is a request to Commercial to slow a launch. It is drawn in the same shape
        as the other levers so it can be weighed against them, but committing it means asking
        someone else for permission.
      </div>
    </>
  );
}
